import { TEXT_SELECTOR, bgUrl, findHeadline, imgSrc, isOwn, preview, selectorFor, textOf } from "./dom";
import { dict, type Lang } from "./i18n";
import type { App } from "./index";
import { OUTCOME_LABEL, SITE_ARTICLE_FP, assess, articleFingerprint, srcFingerprint, textFingerprint, type AssessAnswers, type MarkKind, type Origin, type PublicMark } from "./shared";
import { h } from "./ui";

const STORE = "aimarker-edit";
const TOOLS = ["ChatGPT", "Claude", "Gemini", "Midjourney", "DALL·E", "Adobe Firefly", "Stable Diffusion", "FLUX", "Copilot", "Jasper", "Canva"];

interface Target {
  el: Element;
  kind: MarkKind;
  fingerprint: string;
}

/** Live "mark on page" mode, activated with #aimarker-edit=<token> from the dashboard. */
export class Editor {
  private token: string | null = null;
  private userName = "";
  private hl: HTMLElement | null = null;
  private bar: HTMLElement | null = null;
  private panel: HTMLElement | null = null;
  private countEl: HTMLElement | null = null;
  private modeEl: HTMLElement | null = null;
  private hintEl: HTMLElement | null = null;
  private toggleBtn: HTMLButtonElement | null = null;
  /** Browse mode: clicks behave normally so you can move between pages. */
  private browsing = false;

  /** Editor UI language: the dashboard language of the person who opened labelling mode, else the page language */
  private lang: Lang;

  constructor(private app: App) {
    this.lang = app.lang;
  }

  async maybeStart() {
    const m = location.hash.match(/aimarker-edit=([\w-]+)/);
    const ml = location.hash.match(/aimarker-lang=(pl|en)/);
    try {
      if (ml) sessionStorage.setItem(`${STORE}-lang`, ml[1]);
      const stored = sessionStorage.getItem(`${STORE}-lang`);
      if (stored === "pl" || stored === "en") this.lang = stored;
    } catch {
      if (ml) this.lang = ml[1] as Lang;
    }
    if (m) {
      try {
        sessionStorage.setItem(STORE, m[1]);
      } catch {
        /* storage blocked: token lives for this page view only */
      }
      this.token = m[1];
      history.replaceState(history.state, "", location.pathname + location.search);
    } else {
      try {
        this.token = sessionStorage.getItem(STORE);
      } catch {
        this.token = null;
      }
    }
    if (!this.token) return;

    const res = await this.api("GET", "/api/v1/edit/session").catch(() => null);
    const data = res && res.ok ? ((await res.json()) as { site: { publicId: string }; user: { name: string } }) : null;
    if (!data || data.site.publicId !== this.app.siteId) {
      this.stop();
      this.toast(dict(this.lang).edit.invalid, 6000);
      return;
    }
    this.userName = data.user.name;
    this.start();
  }

  private api(method: string, path: string, body?: unknown) {
    return fetch(`${this.app.apiBase}${path}`, {
      method,
      credentials: "omit",
      headers: { "X-AIMarker-Token": this.token ?? "", ...(body ? { "Content-Type": "application/json" } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  private start() {
    const d = dict(this.lang).edit;
    this.app.editing = true;
    this.app.apply();

    this.hl = h("div", { class: "hl" });
    this.hl.style.display = "none";
    this.countEl = h("span", { class: "mu" });
    this.modeEl = h("span", {});
    this.hintEl = h("span", { class: "mu" });
    this.toggleBtn = h("button", { class: "btn", type: "button", onclick: () => this.setBrowsing(!this.browsing) }) as HTMLButtonElement;
    this.bar = h(
      "div",
      { class: "bar", role: "toolbar" },
      this.modeEl,
      this.hintEl,
      this.countEl,
      h("span", { class: "mu" }, `${d.as} ${this.userName}`),
      h("button", { class: "btn p", type: "button", title: d.articleHint, onclick: () => this.openArticle() }, d.article),
      this.toggleBtn,
      h("button", { class: "btn", type: "button", onclick: () => this.stop(true) }, d.exit),
    );
    this.app.layer.view.append(this.hl, this.bar);
    this.setBrowsing(false);
    this.updateCount();

    document.addEventListener("mousemove", this.onMove, true);
    document.addEventListener("click", this.onClick, true);
  }

  private stop(reload = false) {
    try {
      sessionStorage.removeItem(STORE);
      sessionStorage.removeItem(`${STORE}-lang`);
    } catch {
      /* ignore */
    }
    document.removeEventListener("mousemove", this.onMove, true);
    document.removeEventListener("click", this.onClick, true);
    this.hl?.remove();
    this.bar?.remove();
    this.panel?.remove();
    this.panel = null;
    if (this.app.editing) {
      this.app.editing = false;
      if (reload) this.app.apply();
    }
  }

  private setBrowsing(on: boolean) {
    const d = dict(this.lang).edit;
    this.browsing = on;
    if (on) this.closePanel();
    if (this.hl) this.hl.style.display = "none";
    if (this.modeEl) {
      this.modeEl.replaceChildren(h("b", { style: on ? "color:#a0a0b4" : undefined }, on ? "○ " : "● "), on ? d.browsing : d.mode);
    }
    if (this.hintEl) this.hintEl.textContent = on ? d.hintBrowse : d.hint;
    if (this.toggleBtn) this.toggleBtn.textContent = on ? d.label : d.browse;
  }

  private updateCount() {
    if (this.countEl) this.countEl.textContent = `· ${dict(this.lang).edit.count(this.app.decorations.filter((x) => x.mark).length)}`;
  }

  /**
   * Pick what's under the pointer: a text block, an <img>, or an element with a CSS background image.
   * Uses elementsFromPoint so images hidden under overlays/links are still reachable.
   * forceBg (Shift) skips text and picks the image/background underneath.
   */
  private target(e: MouseEvent, forceBg = false): Target | null {
    const stack = document.elementsFromPoint(e.clientX, e.clientY).filter((el) => !isOwn(el));
    if (!stack.length) return null;

    if (!forceBg) {
      const block = stack[0].closest(TEXT_SELECTOR);
      if (block && !isOwn(block) && !block.querySelector(TEXT_SELECTOR) && textOf(block).trim().length >= 12) {
        return { el: block, kind: "TEXT", fingerprint: textFingerprint(textOf(block)) };
      }
    }
    for (const el of stack) {
      if (el === document.documentElement || el === document.body) break;
      if (el instanceof HTMLImageElement && imgSrc(el)) return { el, kind: "IMAGE", fingerprint: srcFingerprint(imgSrc(el), location.href) };
      const bg = bgUrl(el);
      if (bg) return { el, kind: "IMAGE", fingerprint: srcFingerprint(bg, location.href) };
    }
    return null;
  }

  private onMove = (e: MouseEvent) => {
    if (!this.hl || this.panel || this.browsing) return;
    if (e.composedPath().includes(this.app.layer.host)) return;
    const t = this.target(e, e.shiftKey);
    if (!t) {
      this.hl.style.display = "none";
      return;
    }
    const r = t.el.getBoundingClientRect();
    Object.assign(this.hl.style, { display: "block", left: `${r.left - 4}px`, top: `${r.top - 4}px`, width: `${r.width + 8}px`, height: `${r.height + 8}px` });
  };

  private onClick = (e: MouseEvent) => {
    if (e.composedPath().includes(this.app.layer.host)) return;
    // Browse mode or Alt/Option+click: let links and buttons work normally
    if (this.browsing || e.altKey) return;
    const t = this.target(e, e.shiftKey);
    // Swallow clicks on the host page (links, buttons) while labelling
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    if (t) this.open(t);
  };

  private openArticle() {
    const headline = findHeadline();
    if (!headline) return this.toast(dict(this.lang).edit.error);
    this.setBrowsing(false);
    headline.scrollIntoView({ block: "center", behavior: "smooth" });
    this.open({ el: headline, kind: "ARTICLE", fingerprint: articleFingerprint(location.pathname) });
  }

  private existing(t: Target): PublicMark | undefined {
    const own = (fp: string) => this.app.marks.find((m) => !m.id.startsWith("asset:") && m.kind === t.kind && m.fingerprint === fp);
    return own(t.fingerprint) ?? (t.kind === "ARTICLE" ? own(SITE_ARTICLE_FP) : undefined);
  }

  private open(t: Target) {
    const d = dict(this.lang).edit;
    this.panel?.remove();
    const ex = this.existing(t);
    const r = t.el.getBoundingClientRect();
    if (this.hl) Object.assign(this.hl.style, { display: "block", left: `${r.left - 4}px`, top: `${r.top - 4}px`, width: `${r.width + 8}px`, height: `${r.height + 8}px` });
    this.hl?.classList.add("on");

    const origins: Origin[] = ["AI_GENERATED", "AI_EDITED", "HUMAN"];
    const current = ex?.origin ?? "AI_GENERATED";
    const radio = (name: string, value: string, label: string, checked: boolean) =>
      h("label", { class: "opt" }, h("input", { type: "radio", name, value, checked }), label);

    const listId = "aimarker-tools";
    const tool = h("input", { class: "in", name: "tool", list: listId, placeholder: d.toolPh, value: ex?.tool ?? "", maxlength: "120" }) as HTMLInputElement;
    const datalist = h("datalist", { id: listId }, ...TOOLS.map((x) => h("option", { value: x })));
    const reviewed = h("input", { type: "checkbox", name: "reviewed", checked: !!ex?.reviewedBy }) as HTMLInputElement;

    const assessBox = this.assessBox(t.kind);
    const form = h(
      "form",
      {},
      h("h5", {}, dict(this.lang).title[t.kind][current]),
      h("p", { class: "pv" }, t.kind === "ARTICLE" ? `${preview(t.el)} — ${d.articleHint}` : preview(t.el)),
      h("label", { class: "l" }, d.origin),
      h("div", { class: "seg" }, ...origins.map((o) => radio("origin", o, dict(this.lang).title[t.kind][o], o === current))),
      h("label", { class: "l" }, d.tool),
      tool,
      datalist,
      h("label", { class: "chk" }, reviewed, d.reviewed),
      assessBox.el,
      t.kind === "IMAGE" || t.kind === "ARTICLE"
        ? h(
            "div",
            {},
            h("label", { class: "l" }, d.scope),
            h(
              "div",
              { class: "seg" },
              radio("scope", "page", d.scopePage, ex ? ex.pagePath !== "*" : true),
              radio("scope", "site", t.kind === "ARTICLE" ? d.scopeSiteArticle : d.scopeSite, ex?.pagePath === "*"),
            ),
          )
        : null,
      h(
        "div",
        { class: "acts" },
        h("button", { class: "btn p", type: "submit" }, d.save),
        ex ? h("button", { class: "btn d", type: "button", onclick: () => void this.remove(ex) }, d.remove) : null,
        h("span", { class: "sp" }),
        h("button", { class: "btn", type: "button", onclick: () => this.closePanel() }, d.cancel),
      ),
    ) as HTMLFormElement;

    form.addEventListener("change", () => {
      const o = (form.elements.namedItem("origin") as RadioNodeList).value as Origin;
      (form.querySelector("h5") as HTMLElement).textContent = dict(this.lang).title[t.kind][o];
    });
    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const origin = (form.elements.namedItem("origin") as RadioNodeList).value as Origin;
      const scope = t.kind === "IMAGE" || t.kind === "ARTICLE" ? (form.elements.namedItem("scope") as RadioNodeList).value : "page";
      void this.save(t, ex, { origin, tool: tool.value.trim() || null, reviewed: reviewed.checked, scope, assessment: assessBox.answers() });
    });

    this.panel = h("div", { class: "panel", role: "dialog" }, form);
    this.app.layer.view.append(this.panel);
    tool.focus({ preventScroll: true });
  }

  /** Compact "does this need a label?" questions inside the labelling panel, with a live result. */
  private assessBox(kind: MarkKind): { el: HTMLElement; answers: () => AssessAnswers | null } {
    const d = dict(this.lang).edit.assess;
    const media = kind === "IMAGE";
    const industry = this.app.policy.industry;
    const medical = industry === "medical";
    const sel = (name: string, label: string, opts: Array<[string, string]>) => {
      const s = h("select", { class: "in", name: `as_${name}` }, h("option", { value: "" }, d.pick), ...opts.map(([v, l]) => h("option", { value: v }, l))) as HTMLSelectElement;
      return { name, row: h("label", { class: "q" }, h("span", {}, label), s), s };
    };
    const yn: Array<[string, string]> = [
      ["true", d.yes],
      ["false", d.no],
    ];
    const qs = media
      ? [
          sel("realistic", d.realistic, [["yes", d.yes], ["no", d.no], ["unsure", d.unsure]]),
          sel("people", d.people, yn),
          sel("artistic", d.artistic, yn),
          ...(medical ? [sel("clinical", d.clinical, yn)] : []),
        ]
      : [
          sel("aiShare", d.aiShare, [["generated", d.generated], ["substantial", d.substantial], ["minor", d.minor]]),
          sel("publicInterest", d.publicInterest, yn),
          sel("humanReview", d.humanReview, yn),
          sel("editorialResponsibility", d.editorial, yn),
        ];
    if (!media && (medical || industry === "finance_legal")) {
      const pi = qs.find((q) => q.name === "publicInterest");
      if (pi) pi.s.value = "true";
    }
    const res = h("div", { class: "res" });
    const read = (): AssessAnswers => {
      const a: Record<string, unknown> = { kind: media ? "IMAGE" : "TEXT", industry };
      for (const q of qs) if (q.s.value) a[q.name] = q.s.value === "true" ? true : q.s.value === "false" ? false : q.s.value;
      return a as unknown as AssessAnswers;
    };
    const complete = (a: AssessAnswers) =>
      media
        ? (a.realistic === "no" || (a.realistic !== undefined && a.people !== undefined && a.artistic !== undefined)) && (!medical || a.clinical !== undefined)
        : a.aiShare === "minor" || (a.aiShare !== undefined && (a.publicInterest === false || (a.publicInterest === true && (a.humanReview === false || a.editorialResponsibility !== undefined))));
    const update = () => {
      const a = read();
      // Show only the questions that still matter
      for (const q of qs) {
        const hide = media
          ? ["people", "artistic"].includes(q.name) && a.realistic === "no"
          : (q.name !== "aiShare" && a.aiShare === "minor") || (["humanReview", "editorialResponsibility"].includes(q.name) && a.publicInterest === false) || (q.name === "editorialResponsibility" && a.humanReview === false);
        q.row.style.display = hide ? "none" : "";
      }
      if (!complete(a)) return void res.replaceChildren();
      const r = assess(a);
      res.className = `res ${r.outcome}`;
      res.replaceChildren(h("b", {}, OUTCOME_LABEL[r.outcome][this.lang]), h("span", {}, r.reason[this.lang]));
    };
    const el = h("details", { class: "asx" }, h("summary", {}, d.title), ...qs.map((q) => q.row), res, h("p", { class: "nt" }, d.note));
    el.addEventListener("change", (e) => {
      e.stopPropagation();
      update();
    });
    return { el, answers: () => (complete(read()) ? read() : null) };
  }

  private closePanel() {
    this.panel?.remove();
    this.panel = null;
    this.hl?.classList.remove("on");
    if (this.hl) this.hl.style.display = "none";
  }

  private async save(t: Target, ex: PublicMark | undefined, v: { origin: Origin; tool: string | null; reviewed: boolean; scope: string; assessment: AssessAnswers | null }) {
    const d = dict(this.lang).edit;
    const pagePath = v.scope === "site" ? "*" : this.app.pagePath();
    const res = await this.api("POST", "/api/v1/edit/marks", {
      pagePath,
      kind: t.kind,
      selector: selectorFor(t.el),
      fingerprint: t.kind === "ARTICLE" && v.scope === "site" ? SITE_ARTICLE_FP : t.fingerprint,
      preview: t.kind === "ARTICLE" && v.scope === "site" ? `${location.origin}/*` : preview(t.el),
      origin: v.origin,
      tool: v.tool,
      reviewed: v.reviewed,
      // A page-level article label overrides the site-wide one instead of deleting it
      replaceId: ex && ex.pagePath !== pagePath && !(t.kind === "ARTICLE" && ex.pagePath === "*") ? ex.id : undefined,
      assessment: v.assessment ?? undefined,
    }).catch(() => null);
    if (res?.status === 402) return this.toast(d.limit, 6000);
    if (!res?.ok) return this.toast(d.error);
    this.closePanel();
    await this.app.reload();
    this.updateCount();
    this.toast(`✓ ${d.saved}`);
  }

  private async remove(m: PublicMark) {
    const d = dict(this.lang).edit;
    const res = await this.api("DELETE", `/api/v1/edit/marks/${encodeURIComponent(m.id)}`).catch(() => null);
    if (!res?.ok) return this.toast(d.error);
    this.closePanel();
    await this.app.reload();
    this.updateCount();
    this.toast(d.removed);
  }

  private toast(msg: string, ms = 2200) {
    const el = h("div", { class: "toast", role: "status" }, msg);
    this.app.layer.view.append(el);
    setTimeout(() => el.remove(), ms);
  }
}
